<template>
  <div class="hb-sections">
    <div class="hb-sections__header">
      <h2 class="hb-sections__title">أقسام الصفحة</h2>
      <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="addOpen = true">
        <i class="pi pi-plus"></i> إضافة قسم
      </button>
    </div>

    <Loading v-if="store.loading" />

    <!-- vuedraggable (SortableJS wrapper) — matches the reference
         implementation's drag mechanism exactly, rather than a hand-rolled
         HTML5 drag/drop. `handle` restricts dragging to the .pi-bars icon
         only, so clicking the toggle/edit button never starts a drag. -->
    <draggable
      v-else
      v-model="store.sections"
      class="hb-rows"
      item-key="id"
      handle=".hb-row__handle"
      filter=".hb-row--pinned"
      :prevent-on-filter="false"
      @end="onReorder"
    >
      <template #item="{ element: s }">
        <div class="hb-row" :class="{ 'hb-row--pinned': !isDraggable(s), 'hb-row--selected': selectedKey === s.key }">
          <span class="hb-row__handle" :class="{ 'hb-row__handle--disabled': !isDraggable(s) }">
            <i class="pi pi-bars"></i>
          </span>
          <span class="hb-row__name">{{ sectionLabel(s) }}</span>
          <InputSwitch class="hb-row__switch" :model-value="s.is_active" @update:model-value="toggle(s)" />
          <button class="ds-btn ds-btn--outline ds-btn--sm" @click="$emit('edit', s)">
            <i class="pi pi-pencil"></i> تعديل
          </button>
        </div>
      </template>
    </draggable>

    <AddSectionDialog v-model:visible="addOpen" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import InputSwitch from 'primevue/inputswitch';
import Swal from 'sweetalert2';
import Loading from '@/components/global/loading.vue';
import AddSectionDialog from './AddSectionDialog.vue';
import { useHomepageSectionStore } from '@/stores/useHomepageSectionStore.js';

const TYPE_LABELS = {
  header: 'الهيدر',
  hero: 'Hero / الترحيب',
  banners: 'البانرات الإعلانية',
  quick_access: 'الوصول السريع',
  academic_systems: 'الأنظمة الدراسية',
  statistics: 'الإحصائيات',
  testimonials: 'آراء الطلاب',
  cta: 'دعوة لإجراء (CTA)',
  footer: 'Footer',
};

export default {
  name: 'HomepageSectionsList',
  components: { draggable, AddSectionDialog, InputSwitch, Loading },
  props: {
    // Currently-open section's `key`, so this list can highlight it the
    // same way the live preview does (see PreviewPanel's section-click
    // handling) — both point at the same "selection" concept.
    selectedKey: { type: String, default: null },
  },
  emits: ['edit'],
  data() {
    return {
      addOpen: false,
    };
  },
  computed: {
    store() { return useHomepageSectionStore(); },
  },
  methods: {
    sectionLabel(s) {
      if (s.type === 'home_products') {
        return s.settings?.kind === 'course' ? 'الكورسات' : 'السنوات الدراسية';
      }
      return TYPE_LABELS[s.type] || s.type;
    },
    // footer's sort_order is meaningless (rendered globally, always last) —
    // filtered out of drag entirely, so it can never be misfiled among the
    // real per-page sections.
    isDraggable(s) {
      return s.type !== 'footer';
    },
    // vuedraggable already mutated store.sections into the new order (via
    // v-model) by the time @end fires — persist it, recomputing sort_order
    // from array position, matching the reference reorder() action. Roll
    // back with a full refetch if the API call fails.
    async onReorder() {
      try {
        await this.store.reorder(this.store.sections.map((s) => s.id));
      } catch {
        await this.store.fetchSections();
        Swal.fire({ icon: 'error', title: 'تعذّر حفظ الترتيب', timer: 2000, showConfirmButton: false });
      }
    },
    async toggle(s) {
      try {
        await this.store.toggleActive(s.id);
      } catch {
        Swal.fire({ icon: 'error', title: 'تعذّر تغيير الحالة', timer: 2000, showConfirmButton: false });
      }
    },
  },
};
</script>

<style scoped>
.hb-sections__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.hb-sections__title {
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0;
}
.hb-rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.hb-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 0.65rem 0.85rem;
}
.hb-row--selected { outline: 2px solid #2563eb; outline-offset: -2px; }
.hb-row.sortable-ghost { opacity: 0.4; }
.hb-row__handle {
  color: var(--text-secondary, #94a3b8);
  font-size: 0.95rem;
  flex-shrink: 0;
  cursor: move;
}
.hb-row__handle--disabled { opacity: 0.35; cursor: default; }
.hb-row__name {
  flex: 1;
  font-weight: 700;
  min-width: 0;
}
.hb-row__switch { flex-shrink: 0; }
</style>
